import type { StorePasswordRequiredModalProps } from "@v1/types/modalTypes";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { TRPCClientError } from "@trpc/client";
import { setCanvasHtml } from "@v1/components/canvas/canvas-reducer";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import Input from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import {
  setActiveCurrency,
  setActiveLanguage,
  setMoneyFormats,
  setOkendoWidgetNamespace,
  setShopifyUrlRoot,
} from "@v1/reducers/commerce-reducer";
import {
  selectDraftElementId,
  selectStoreUrl,
  setSrcDocFonts,
  setSrcDocFontUrls,
  setSrcDocRootFont,
} from "@v1/reducers/core-reducer";
import { replaceCdnUrls } from "@v1/reducers/utils/api-reducer-utils";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useModalActions } from "@v2/store/modalStore";
import { elementUtils } from "@v2/trpc/routes/element";
import { handleTRPCClientError } from "@v2/utils/error-handling";
import { useForm } from "react-hook-form";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import * as z from "zod";

const formSchema = z.object({
  password: z.string().min(1, "Please provide your store password"),
});
type FormData = z.infer<typeof formSchema>;

export const StorePasswordRequiredModal: React.FC<
  StorePasswordRequiredModalProps
> = ({ type }) => {
  const projectId = useCurrentProjectId();
  const draftElementId = useEditorSelector(selectDraftElementId);
  const storeUrl = useEditorSelector(selectStoreUrl);
  const dispatch = useEditorDispatch();
  const { closeModal } = useModalActions();
  const [isIncorrectPassword, setIsIncorrectPassword] = useOverridableState(
    type === "wrongPassword",
  );
  const [hasAttempted, setHasAttempted] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Note (Noah, 2025-03-18): Weird that we have to do an effect like this, but
  // the state of when there are actually incorrect passwords is currently updated
  // through the props of this modal
  React.useEffect(() => {
    if (!isLoading) {
      if (isIncorrectPassword && !hasAttempted) {
        setError("password", {
          message: "Your Shopify store's password was incorrect.",
        });
      }
      if (isIncorrectPassword && hasAttempted) {
        setError("password", {
          message: "That password was incorrect. Please try again.",
        });
      }
    }
  }, [isIncorrectPassword, hasAttempted, setError, isLoading]);

  const isButtonLoading = isSubmitting || isLoading;

  const onSubmit = async (data: FormData) => {
    if (isButtonLoading || !projectId || !draftElementId) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await elementUtils.getCanvasDocument.fetch(
        {
          projectId,
          elementId: draftElementId,
          storePassword: data.password,
        },
        {
          staleTime: 0,
          meta: { reploIgnoreDefaultErrorHandling: true },
        },
      );
      if (result.status === "success") {
        // NOTE (Patrick, 2026-02-05): Dispatch canvas data directly instead
        // of using setData, because the useAutoRefetchCanvasDocument hook's
        // query uses skipToken at this point and won't pick up cache updates.
        if (result.html) {
          dispatch(
            setCanvasHtml(
              storeUrl ? replaceCdnUrls(result.html, storeUrl) : result.html,
            ),
          );
        }
        if (result.fontUrls) {
          dispatch(setSrcDocFontUrls(result.fontUrls));
        }
        if (result.srcDocFonts) {
          dispatch(setSrcDocFonts(result.srcDocFonts));
        }
        if (result.srcDocRootFont) {
          dispatch(setSrcDocRootFont(result.srcDocRootFont));
        }
        dispatch(setActiveCurrency(result.currencyCode));
        dispatch(
          setMoneyFormats({
            moneyFormat: result.moneyFormat,
            moneyWithCurrencyFormat: result.moneyWithCurrencyFormat,
          }),
        );
        dispatch(setActiveLanguage(result.activeLanguage));
        dispatch(setShopifyUrlRoot(result.activeShopifyUrlRoot));
        dispatch(setOkendoWidgetNamespace(result.okendoNamespace ?? null));
        closeModal({ type: "storePasswordRequiredModal" });
      } else {
        setHasAttempted(true);
        setIsIncorrectPassword(true);
      }
    } catch (error) {
      if (error instanceof TRPCClientError) {
        const isShopifyPasswordError = z
          .object({
            userFacingDetails: z.object({
              type: z.literal("shopifyStorePassword"),
              key: z.enum(["passwordRequired", "wrongPassword"]),
            }),
          })
          .safeParse(error.data).success;

        handleTRPCClientError(error);

        if (isShopifyPasswordError) {
          setHasAttempted(true);
          setIsIncorrectPassword(true);
        }
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      data-testid="store-password-modal-form"
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
    >
      <Modal
        title="Store Password Required"
        isOpen={true}
        onOpenChange={(open) => {
          if (!open) {
            closeModal({ type: "storePasswordRequiredModal" });
          }
        }}
        size="sm"
        description="This Shopify store is password protected. Please enter your store password to edit pages."
        footer={
          <DeprecatedButton
            variant="primary"
            size="base"
            type="submit"
            isLoading={isButtonLoading}
          >
            Submit Password
          </DeprecatedButton>
        }
      >
        <div className="text-muted flex flex-col gap-y-4">
          <DeprecatedLabeledControl
            label="Store Password"
            size="sm"
            error={errors.password?.message}
            description="You can find this password in Shopify Admin under Sales Channels > Online Store > Preferences > Password Protection."
          >
            <Input
              size="base"
              autoFocus={true}
              validityState={errors.password?.message ? "invalid" : undefined}
              placeholder="Password"
              {...register("password")}
            />
          </DeprecatedLabeledControl>
        </div>
      </Modal>
    </form>
  );
};
